import React, { memo } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { TripsGrid } from '../components/TripsGrid';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Button } from '../components/ui/Button';
import { motion } from 'framer-motion';
import { Users, Heart, Camera, Utensils, MapPin, Calendar, ThermometerSun, Sparkles } from 'lucide-react';
const places = [{
  name: 'Kathmandu',
  description: 'Ancient temples, vibrant markets, and rich cultural heritage in the heart of Nepal',
  image: 'https://images.unsplash.com/photo-1626621341517-bbf3d9990a23?q=80&w=2070&auto=format&fit=crop',
  highlights: ['Swayambhunath Temple', 'Durbar Square', 'Boudhanath Stupa']
}, {
  name: 'Pokhara',
  description: 'Serene lakeside paradise with stunning mountain views and adventure activities',
  image: 'https://images.unsplash.com/photo-1605640840605-14ac1855827b?q=80&w=1933&auto=format&fit=crop',
  highlights: ['Phewa Lake', 'Peace Pagoda', 'Paragliding']
}, {
  name: 'Chitwan',
  description: 'Wildlife safari adventures in lush jungle national park',
  image: 'https://images.unsplash.com/photo-1564760055775-d63b17a55c44?q=80&w=2076&auto=format&fit=crop',
  highlights: ['Jungle Safari', 'Elephant Bathing', 'Tharu Culture']
}, {
  name: 'Annapurna',
  description: 'Family-friendly mountain trails with breathtaking Himalayan panoramas',
  image: 'https://images.unsplash.com/photo-1548588627-f978862b85e1?q=80&w=2072&auto=format&fit=crop',
  highlights: ['Easy Treks', 'Mountain Views', 'Local Villages']
}];
const advantages = [{
  icon: Users,
  title: 'Family-Friendly Itineraries',
  description: 'Carefully designed routes suitable for all ages with comfortable pacing and rest days'
}, {
  icon: Heart,
  title: 'Safe & Comfortable',
  description: 'Handpicked family-friendly accommodations with modern amenities and safety standards'
}, {
  icon: Camera,
  title: 'Educational & Fun',
  description: 'Cultural experiences and activities that engage both children and adults'
}, {
  icon: Utensils,
  title: 'Flexible Dining',
  description: 'Variety of meal options including familiar cuisines for picky eaters'
}];
const bestSeasons = [{
  season: 'Spring (March - May)',
  temp: '15°C - 25°C',
  description: 'Perfect weather for family activities with blooming flowers and clear skies',
  ideal: true
}, {
  season: 'Autumn (September - November)',
  temp: '10°C - 20°C',
  description: 'Comfortable temperatures and excellent visibility for mountain views',
  ideal: true
}, {
  season: 'Winter (December - February)',
  temp: '5°C - 15°C',
  description: 'Cool but pleasant in lower elevations, great for Chitwan and Pokhara',
  ideal: false
}];
export function FamilyHolidaysPage() {
  return <div className="min-h-screen bg-white font-sans" data-id="element-1403">
      <Navbar data-id="element-1404" />

      {/* Hero Section */}
      <div className="relative h-[60vh] min-h-[500px] flex items-center justify-center" data-id="element-1405">
        <div className="absolute inset-0 bg-cover bg-center z-0" style={{
        backgroundImage: 'url("https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?q=80&w=2070&auto=format&fit=crop")'
      }} data-id="element-1406">
          <div className="absolute inset-0 bg-gradient-to-b from-blue-900/60 via-blue-900/40 to-blue-900/70" data-id="element-1407" />
        </div>
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto" data-id="element-1408">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="flex justify-center mb-6" data-id="element-1409">
            <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center" data-id="element-1410">
              <Users className="text-white" size={32} data-id="element-1411" />
            </div>
          </motion.div>
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="text-5xl md:text-6xl font-bold mb-6 tracking-tight" data-id="element-1412">
            Family Holidays in Nepal
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} className="text-xl md:text-2xl text-blue-100 font-light max-w-2xl mx-auto" data-id="element-1413">
            Create unforgettable memories with your loved ones in the heart of
            the Himalayas
          </motion.p>
        </div>
      </div>

      {/* Overview Section */}
      <section className="py-20 px-4 md:px-8 max-w-7xl mx-auto" data-id="element-1414">
        <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-1415">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1416">
            <SectionHeading title="Adventures for All Ages" subtitle="Overview" centered={false} data-id="element-1417" />
            <div className="space-y-4 text-lg text-stone-700 leading-relaxed" data-id="element-1418">
              <p data-id="element-1419">
                Nepal is the perfect destination for families seeking adventure,
                culture, and natural beauty. Our family holiday packages are
                thoughtfully designed to balance excitement with comfort,
                ensuring every family member—from young children to
                grandparents—has an enriching and enjoyable experience.
              </p>
              <p data-id="element-1420">
                Explore ancient temples in Kathmandu, paddle on serene Phewa
                Lake in Pokhara, spot rhinos and elephants in Chitwan's jungle,
                and enjoy easy mountain walks with spectacular Himalayan views.
                Each destination offers unique experiences that educate,
                inspire, and create lasting family bonds.
              </p>
              <p data-id="element-1421">
                With comfortable accommodations, flexible itineraries, and
                experienced family-friendly guides, we take care of every detail
                so you can focus on making memories together.
              </p>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="relative" data-id="element-1422">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl" data-id="element-1423">
              <img src="https://images.unsplash.com/photo-1511895426328-dc8714191300?q=80&w=2070&auto=format&fit=crop" alt="Family enjoying Nepal" className="w-full h-full object-cover" data-id="element-1424" />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-gradient-to-br from-blue-500 to-cyan-500 p-6 rounded-xl shadow-lg max-w-xs text-white" data-id="element-1425">
              <p className="font-bold text-2xl mb-1" data-id="element-1426">10,000+</p>
              <p className="text-blue-100 text-sm" data-id="element-1427">
                Happy families have explored Nepal with us
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trips Grid */}
      <section className="bg-stone-50 py-20 px-4" data-id="element-1428">
        <div className="max-w-7xl mx-auto" data-id="element-1429">
          <TripsGrid data-id="element-1430" />
        </div>
      </section>

      {/* Places Covered */}
      <section className="py-20 px-4 max-w-7xl mx-auto" data-id="element-1431">
        <SectionHeading title="Places You'll Explore" subtitle="Discover Nepal's most family-friendly destinations" data-id="element-1432" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1433">
          {places.map((place, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300" data-id="element-1434">
              <div className="relative h-64 overflow-hidden" data-id="element-1435">
                <img src={place.image} alt={place.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-1436" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" data-id="element-1437" />
                <div className="absolute bottom-0 left-0 p-6" data-id="element-1438">
                  <div className="flex items-center gap-2 text-cyan-400 mb-2" data-id="element-1439">
                    <MapPin size={18} data-id="element-1440" />
                    <span className="text-sm font-medium" data-id="element-1441">Nepal</span>
                  </div>
                  <h3 className="text-3xl font-bold text-white mb-2" data-id="element-1442">
                    {place.name}
                  </h3>
                </div>
              </div>
              <div className="p-6" data-id="element-1443">
                <p className="text-stone-600 mb-4 leading-relaxed" data-id="element-1444">
                  {place.description}
                </p>
                <div className="flex flex-wrap gap-2" data-id="element-1445">
                  {place.highlights.map((highlight, idx) => <span key={idx} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium" data-id="element-1446">
                      {highlight}
                    </span>)}
                </div>
              </div>
            </motion.div>)}
        </div>
      </section>

      {/* Best Time to Visit */}
      <section className="bg-gradient-to-br from-blue-50 to-cyan-50 py-20 px-4" data-id="element-1447">
        <div className="max-w-7xl mx-auto" data-id="element-1448">
          <SectionHeading title="Best Time to Visit" subtitle="Plan your family holiday for the perfect weather" data-id="element-1449" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1450">
            {bestSeasons.map((season, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className={`relative bg-white rounded-2xl p-8 ${season.ideal ? 'border-2 border-blue-500 shadow-xl' : 'border border-stone-200 shadow-md'}`} data-id="element-1451">
                {season.ideal && <div className="absolute -top-3 right-6 bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-4 py-1 rounded-full text-sm font-semibold" data-id="element-1452">
                    Recommended
                  </div>}
                <div className="flex items-center gap-3 mb-4" data-id="element-1453">
                  <div className={`p-3 rounded-xl ${season.ideal ? 'bg-blue-100 text-blue-600' : 'bg-stone-100 text-stone-600'}`} data-id="element-1454">
                    <Calendar size={24} data-id="element-1455" />
                  </div>
                  <div data-id="element-1456">
                    <h3 className="text-xl font-bold text-stone-900" data-id="element-1457">
                      {season.season}
                    </h3>
                    <div className="flex items-center gap-2 text-stone-600 text-sm" data-id="element-1458">
                      <ThermometerSun size={16} data-id="element-1459" />
                      <span data-id="element-1460">{season.temp}</span>
                    </div>
                  </div>
                </div>
                <p className="text-stone-600 leading-relaxed" data-id="element-1461">
                  {season.description}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Advantages Section */}
      <section className="relative py-24 px-4 overflow-hidden" style={{
      backgroundImage: 'url("https://images.unsplash.com/photo-1506905925346-21bda4d32df4?q=80&w=2070&auto=format&fit=crop")',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed'
    }} data-id="element-1462">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/95 via-blue-800/90 to-cyan-900/95" data-id="element-1463" />

        <div className="relative z-10 max-w-7xl mx-auto" data-id="element-1464">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-1465">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white/10 backdrop-blur-sm rounded-full mb-6" data-id="element-1466">
              <Sparkles className="text-cyan-400" size={40} data-id="element-1467" />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-1468">
              The Advantage of Taking This Holiday
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto" data-id="element-1469">
              Why families choose our Nepal holiday packages
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-1470">
            {advantages.map((advantage, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8 hover:bg-white/15 transition-all duration-300" data-id="element-1471">
                <div className="w-14 h-14 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center mb-6" data-id="element-1472">
                  <advantage.icon size={28} className="text-white" data-id="element-1473" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3" data-id="element-1474">
                  {advantage.title}
                </h3>
                <p className="text-blue-100 leading-relaxed" data-id="element-1475">
                  {advantage.description}
                </p>
              </motion.div>)}
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mt-16" data-id="element-1476">
            <p className="text-white text-lg mb-6 max-w-2xl mx-auto" data-id="element-1477">
              Join thousands of families who have created lifelong memories
              exploring Nepal's wonders together
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1478">
              <Button variant="primary" size="lg" className="bg-white text-blue-900 hover:bg-blue-50 border-none" data-id="element-1479">
                View Family Packages
              </Button>
              <Button variant="outline" size="lg" className="border-2 border-white text-white hover:bg-white hover:text-blue-900" data-id="element-1480">
                Contact Us
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-white" data-id="element-1481">
        <div className="max-w-4xl mx-auto text-center" data-id="element-1482">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-1483">
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4" data-id="element-1484">
              Ready to Plan Your Family Adventure?
            </h2>
            <p className="text-xl text-stone-600 mb-8" data-id="element-1485">
              Let our family travel experts help you create the perfect Nepal
              holiday for your loved ones
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1486">
              <a href="#plan-trip" data-id="element-1487">
                <Button variant="primary" size="lg" data-id="element-1488">
                  Start Planning
                </Button>
              </a>
              <a href="#contact" data-id="element-1489">
                <Button variant="outline" size="lg" className="border-stone-300 text-stone-700 hover:bg-stone-100" data-id="element-1490">
                  Get Expert Advice
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer data-id="element-1491" />
    </div>;
}